'use client';

/**
 * MagicLinkForm — email-input + send-link CTA for the magic-link
 * auth flow.
 *
 * Used by:
 *   - LoginScreen (full-page, no authenticated session)
 *   - WebTopNavCard's Sign-in popover (anonymous Klyde Public session
 *     transitioning to authenticated)
 *
 * POSTs to /auth/email/request via api.requestMagicLink(). The backend
 * always 200s with the same shape regardless of whether the email is
 * registered — registered actors receive a link, unknown emails get
 * nothing. The "Check your inbox" screen renders the same either way
 * (deliberate, prevents account-enumeration via this endpoint).
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain auth
 */

import { useState } from 'react';
import { api } from '@/lib/api/client';

interface MagicLinkFormProps {
  /** Fires after a successful send, after the user dismisses the success state. */
  onClose?: () => void;
}

export function MagicLinkForm({ onClose }: MagicLinkFormProps) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send link');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center space-y-2 py-2">
        <p className="text-sm font-medium">Check your inbox</p>
        <p className="text-xs text-muted-foreground">
          If <span className="font-mono">{email}</span> is registered, a sign-in link is on the way.
        </p>
        <button
          onClick={() => { setSent(false); setEmail(''); onClose?.(); }}
          className="text-xs text-muted-foreground underline hover:text-foreground transition-colors"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        required
        disabled={submitting}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
      />
      <button
        type="submit"
        disabled={submitting || !email.trim()}
        className="w-full px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
      >
        {submitting ? 'Sending...' : 'Send sign-in link'}
      </button>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </form>
  );
}
